<template>
  <section
    class="c_wrapper c_accordion_v2"
    :class="{ 'c_accordion_v2--columns': data.enableColumns }"
    :style="accordionThemeVars"
  >
    <div class="c_accordion__parent_description">
      <Chip v-if="data.chip" theme="var(--color-yellow)">{{ data.chip }}</Chip>
      <h2 class="c_2022--h2_graphik" v-if="data.sectionTitle">{{ data.sectionTitle }}</h2>
      <div class="c_2022--body" v-if="data.sectionDescription" v-html="data.sectionDescription"></div>
    </div>
    <accordion>
      <accordion-item
        v-for="(accordion, index) in data.accordions"
        :key="index"
        :item-index="index"
        :theme="data.colors === 'blackTextWhiteBackground' ? 'light' : 'dark'"
        open-behavior="none"
        :allow-multiple="data.allowMultiple"
        @requestOpen="openAt($event)"
        :ref="setItems"
      >
        <accordion-title slot="title" v-if="accordion.accordionTitle">
          <div class="c_accordion_v2__title">
            <IconSVG
              v-if="accordion.icon !== 'none'"
              v-bind="getAccordionIcon(accordion.icon)"
              width="16"
              height="16"
            />
            <span>{{ accordion.accordionTitle }}</span>
          </div>
        </accordion-title>
        <accordion-content slot="content">
          <section class="c_2022--body c_text">
            <div v-if="accordion.accordionDescription" v-html="accordion.accordionDescription"></div>
            <ul v-if="accordion.ctaLinks" class="c_accordion__ctas">
              <li v-for="(link, index) in accordion.ctaLinks" :key="index" v-if="link.linkUrl">
                <a
                  :href="link.linkUrl"
                  :target="link.openInANewTab ? '_blank' : ''"
                  :aria-label="link.linkLabel"
                  :title="link.linkLabel"
                  >{{ link.linkLabel }}</a
                >
              </li>
            </ul>
          </section>
        </accordion-content>
      </accordion-item>
    </accordion>
  </section>
</template>

<script>
import { defineComponent, ref, onBeforeUpdate } from '@nuxtjs/composition-api';
import Chip from 'Components/chip/Chip.vue';
import IconSVG from 'Components/icon_svg/IconSVG.vue';
import Accordion from 'Components/atom_accordion/Accordion.vue';
import AccordionContent from 'Components/atom_accordion/AccordionContent.vue';
import AccordionItem from 'Components/atom_accordion/AccordionItem.vue';
import AccordionTitle from 'Components/atom_accordion/AccordionTitle.vue';

export default defineComponent({
  name: 'AccordionV2',
  props: {
    data: Object,
  },
  components: {
    Chip,
    IconSVG,
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTitle,
  },
  setup(props) {
    const c_accordion_items = ref([]);
    const setItems = (el) => {
      if (el) c_accordion_items.value.push(el);
    };
    onBeforeUpdate(() => {
      c_accordion_items.value = [];
    });

    const getAccordionIcon = (icon) => {
      const icons = {
        checkmark: { handle: 'checkmark', color: '#62912D', height: '13', width: '25' },
        crossmark: { handle: 'closeV2', color: '#ED0000', height: '13', width: '25' },
      };
      return icons[icon];
    };

    const accordionThemeVars =
      props.data.colors === 'whiteTextBlackBackground'
        ? {
            '--accordion-bg': 'var(--color-black)',
            '--accordion-text': 'var(--color-white)',
            '--accordion-divider': 'var(--color-yellow)',
            '--accordion-link-text': 'var(--color-yellow)',
            '--accordion-link-hover': 'var(--color-yellow-06)',
            '--accordion-btn-hover': 'rgba(255,255,255,0.08)',
          }
        : {
            '--accordion-bg': 'var(--color-white)',
            '--accordion-text': '#1c1c1c',
            '--accordion-divider': 'var(--color-gray_230)',
            '--accordion-link-text': 'var(--color-red)',
            '--accordion-link-hover': 'var(--color-red_179)',
            '--accordion-btn-hover': 'rgba(0,0,0,0.03)',
          };

    const closeAllAccordions = (currentIndex) => {
      c_accordion_items.value.forEach((child) => child?.closeAll(currentIndex));
    };

    const openAt = (indexToOpen) => {
      c_accordion_items.value.forEach((child) => child?.closeAll());
      const child = c_accordion_items.value[indexToOpen];
      child?.openSpecific(indexToOpen);
    };

    return {
      getAccordionIcon,
      accordionThemeVars,
      closeAllAccordions,
      setItems,
      openAt,
    };
  },
});
</script>

<style lang="scss">
.c_accordion_v2 {
  color: var(--accordion-text);
  a {
    color: var(--accordion-link-text);
    transition: all 0.3s;
    &:hover,
    &:focus {
      color: var(--accordion-link-hover);
    }
  }
  .c_atom_accordion {
    max-width: 768px;
  }
  &--columns {
    @include mq(900) {
      display: flex;
      align-items: flex-start;
      justify-content: center;
      .c_accordion__parent_description {
        flex-basis: 50%;
        padding-right: px_to_rem(64);
        ul,
        ol {
          margin-left: 0;
          list-style-position: outside;
          padding-left: 1rem;
        }
      }
      .c_atom_accordion {
        flex-basis: 50%;
      }
    }
  }
  &__title {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .c_atom_accordion__content {
    padding: px_to_rem(16) 0;
  }
  .c_accordion__parent_description {
    margin-bottom: px_to_rem(50);
    max-width: 768px;
  }
  .c_2022--h2_graphik {
    @include font('graphik_semibold');
    margin-bottom: px_to_rem(16);
  }
  .c_accordion__item {
    margin-bottom: px_to_rem(16);
  }

  table {
    @include table_styling;
    $_border: 1px solid $color_gray_191;
    box-sizing: border-box;
    border: $_border;
    overflow: auto;
    font-size: var(--text-fz);
    th {
      border-bottom: $_border;
    }
  }
  .c_2022--body {
    --text-fz: 0.95rem;
    line-height: px_to_rem(18);
    p {
      margin-bottom: px_to_rem(16);
    }
    line-height: 26px;
  }
  ul,
  ol {
    margin-left: 0;
    list-style-position: outside;
    padding-left: 1rem;
  }
  .c_accordion__ctas {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: px_to_rem(20);
    margin-top: px_to_rem(16);
    li {
      list-style-type: none;
      margin: 0;
      a {
        @include font('graphik_semibold');
        text-decoration: none;
        font-size: px_to_rem(16);
      }
    }
  }
}
</style>
